import { Theme, css } from '@emotion/react';
import styled from '@emotion/styled';
import { IconButtonProps } from './IconButton.types';

export interface StyledIconButtonProps {
  size: IconButtonProps['size'];
  layoutType: NonNullable<IconButtonProps['layoutType']>;
  colorType: NonNullable<IconButtonProps['colorType']>;
}

const getButtonSize = (size: StyledIconButtonProps['size']) => {
  switch (size) {
    case 'large':
      return css`
        width: 56px;
        height: 56px;
        border-radius: 16px;
      `;

    case 'small':
      return css`
        width: 32px;
        height: 32px;
        border-radius: 8px;
      `;
    case 'medium':
    default:
      return css`
        width: 44px;
        height: 44px;
        border-radius: 12px;
      `;
  }
};

const getButtonContentColor = (
  layoutType: StyledIconButtonProps['layoutType'],
  colorType: StyledIconButtonProps['colorType'],
  theme: Theme,
) => {
  switch (layoutType) {
    case 'primary':
      switch (colorType) {
        case 'basic':
          return css`
            color: ${theme.content.hierarchy1_reverse};
          `;

        case 'negative':
          return css`
            color: ${theme.always.label_white};
          `;
        case 'brand':
        default:
          return css`
            color: ${theme.always.label_white};
          `;
      }

    case 'secondary':
    case 'tertiary':
      switch (colorType) {
        case 'basic':
          return css`
            color: ${theme.content.hierarchy1};
          `;

        case 'negative':
          return css`
            color: ${theme.themeColor.negative};
          `;
        case 'brand':
        default:
          return css`
            color: ${theme.themeColor.brand};
          `;
      }
  }
};

const getButtonStyle = (
  layoutType: StyledIconButtonProps['layoutType'],
  colorType: StyledIconButtonProps['colorType'],
  theme: Theme,
) => {
  switch (layoutType) {
    case 'secondary':
      return css`
        border: 1px solid ${theme.themeColor[`${colorType}`]};
        ${getButtonContentColor(layoutType, colorType, theme)}

        &:hover {
          border: 1px solid ${theme.themeColor[`${colorType}_dark`]};
          background-color: ${theme.themeColor[`${colorType}_tint`]};
        }

        &:disabled {
          border: 1px solid ${theme.backgroundColor.disabled_border};
          background-color: ${theme.backgroundColor.disabled};
        }

        &.on {
          border: 1px solid ${theme.themeColor[`${colorType}_dark`]};
          background: none;
        }
      `;

    case 'tertiary':
      return css`
        border: none;
        ${getButtonContentColor(layoutType, colorType, theme)}

        &:hover {
          background-color: ${theme.themeColor[`${colorType}_tint`]};
        }

        &:disabled {
          background: none;
        }

        &.on {
          background: none;
        }
      `;

    case 'primary':
    default:
      return css`
        border: none;
        background-color: ${theme.themeColor[`${colorType}`]};
        ${getButtonContentColor(layoutType, colorType, theme)}

        &:hover {
          background-color: ${theme.themeColor[`${colorType}_dark`]};
        }

        &:disabled {
          background-color: ${theme.backgroundColor.disabled};
        }

        &.on {
          border: 1px solid ${theme.themeColor[`${colorType}_border`]};
          background-color: ${theme.themeColor[`${colorType}_dark`]};
        }
      `;
  }
};

export const Button = styled.button<StyledIconButtonProps>`
  ${({ theme, size, layoutType, colorType }) => css`
    display: flex;
    align-items: center;
    justify-content: center;

    ${getButtonSize(size)}
    ${getButtonStyle(layoutType, colorType, theme)}

    &:disabled {
      color: ${theme.content.btn_disabled};
      cursor: not-allowed;
    }
  `}
`;
